import React from 'react';
import {
  Alert, Button, Checkbox, ConfirmDialog, SegmentedControl, Select, TextInput, Textarea, phone, useToast,
} from '@parsina/framework';
import { CURRENCY_CODES } from '@parsina/framework/ui/lib/currency';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { gql } from '../../lib/api';
import TaskDialog from '../../components/TaskDialog';
import { FALLBACK_COUNTRY, LANGUAGES, WEEKDAYS, isNotRead } from '../../constants/countryProfile';
import {
  changesFrom, confirmationsFor, draftFrom, insuranceCatalogChoices, insurerCountriesOf, isNull, validateDraft,
} from '../../lib/countryProfileView';
import { INSURER_COUNTRIES_QUERY, UPDATE_COUNTRY_PROFILE } from './queries';
import {
  CeilingNote, NotReadBadge, countryErrorMessage, nullMeaning, useColumnLabel, useCountryName, useValueString,
} from './shared';

export const NOTE_MIN = 3;
export const NOTE_MAX = 500;

/**
 * Edit ONE group of one country's row (CONTRACT.md §2 groups), with a required
 * change note.
 *
 * ── ONLY WHAT CHANGED IS SENT ───────────────────────────────────────────────
 * UpsertCountryProfile is a full replace. The BFF re-reads the stored row and
 * writes back every column this dialog did not touch exactly as stored — NULLs
 * included — so the dialog sends `changes` (the fields that moved) and the
 * row's `updatedAt` as it loaded it; a row that moved since is CONFLICT, not a
 * silent overwrite of someone else's edit.
 *
 * ── NULL IS A CHOICE, SAID FOR WHAT IT DOES ─────────────────────────────────
 * A default's first option is «پیرو *» and names what that resolves to — `*`'s
 * value, or, where `*` has none, what the column's consumer then does (for the
 * dial code: a number without + is refused; `countries.nullMeans`). A
 * capability's first option is «هیچ». The weekend and the map have their own
 * «پیرو * / خودش» switch, because their NULL and their empty differ.
 *
 * ── A CHANGE CLINICS FEEL ASKS TWICE ────────────────────────────────────────
 * A save that takes a capability value away (IR's council registry, its
 * e-prescription …) or clears or changes a default a gate reads today (the dial
 * code, the national-id rule, the address model, the weekend, its zone, the
 * currency) lists what it does and needs an explicit acknowledgement on top of
 * the note. The BFF refuses the same save without those confirmations, so no
 * client can skip it.
 */
export default function EditGroupDialog({
  open, onOpenChange, group, country, row, fallback, deployment, onSaved,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const countryName = useCountryName();
  const valueString = useValueString();
  const columnLabel = useColumnLabel();

  const [draft, setDraft] = React.useState(() => draftFrom(group.fields, row));
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirming, setConfirming] = React.useState(null);
  const [acknowledged, setAcknowledged] = React.useState(false);
  // The countries with insurer rows; null = not read (yet, or at all).
  const [insurerCountries, setInsurerCountries] = React.useState(null);

  // A fresh form every time it opens, from the row as it is NOW.
  React.useEffect(() => {
    if (!open) return;
    setDraft(draftFrom(group.fields, row));
    setNote('');
    setError(null);
    setConfirming(null);
    setAcknowledged(false);
  }, [open, group, row]);

  // `insuranceCatalog` offers the catalogues that exist (PM-0246), read when
  // the group that holds it opens; unreadable, it falls back to IR and IQ.
  const offersCatalog = group.fields.some((f) => f.field === 'insuranceCatalog');
  React.useEffect(() => {
    if (!open || !offersCatalog) return undefined;
    let live = true;
    setInsurerCountries(null);
    gql(INSURER_COUNTRIES_QUERY)
      .then((d) => { if (live) setInsurerCountries(insurerCountriesOf(d?.countryInsurers)); })
      .catch(() => { if (live) setInsurerCountries(null); });
    return () => { live = false; };
  }, [open, offersCatalog]);

  const isFallbackRow = country === FALLBACK_COUNTRY;
  // The kit catalogue's calling code for this country, OFFERED beside an empty
  // dial-code field — one click fills it, nothing fills it silently.
  const dialSuggestion = isFallbackRow ? null : (phone.findByIso2?.(country)?.dial || null);
  const changes = changesFrom(group.fields, draft, row);
  const changed = Object.keys(changes).length > 0;
  const fieldErrors = validateDraft(group.fields, draft);
  const invalid = Object.keys(fieldErrors).length > 0;
  const trimmed = note.trim();
  const noteOk = trimmed.length >= NOTE_MIN && trimmed.length <= NOTE_MAX;
  const groupName = t(`countries.groups.${group.key}`);
  const name = countryName(country);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  /** What a NULL in this row means for one default: `*`'s value, or what the consumer does with none. */
  const nullLabel = (spec) => {
    if (isFallbackRow) return t('countries.edit.noValue', { meaning: nullMeaning(t, spec.field) });
    const resolved = fallback ? fallback[spec.field] : null;
    return isNull(resolved)
      ? t('countries.edit.followNull', { meaning: nullMeaning(t, spec.field) })
      : t('countries.edit.followValue', { value: valueString(spec, resolved) });
  };

  /** A value as the confirmation states it — NULL by what it then means. */
  const stated = (spec, v) => (isNull(v) ? nullLabel(spec) : valueString(spec, v));

  const save = async (confirmations) => {
    setPending(true);
    setError(null);
    try {
      const res = await gql(UPDATE_COUNTRY_PROFILE, {
        country,
        changes,
        note: trimmed,
        expectedUpdatedAt: row?.updatedAt ?? null,
        confirm: confirmations.map((c) => c.token),
      });
      setConfirming(null);
      onOpenChange(false);
      toast.success(t('countries.edit.success', { group: groupName }));
      onSaved?.(res?.updateCountryProfile);
    } catch (e) {
      setConfirming(null);
      setError(countryErrorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const submit = () => {
    if (!changed || invalid || !noteOk || pending) return;
    const needed = confirmationsFor(row, changes);
    if (needed.length) {
      setAcknowledged(false);
      setConfirming(needed);
      return;
    }
    save([]);
  };

  const footerNote = !changed
    ? t('countries.edit.noChanges')
    : (!noteOk ? t('countries.edit.noteNeeded') : undefined);

  return (
    <>
      <TaskDialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('countries.edit.title', { group: groupName, country: name })}
        lead={group.key === 'capabilities' ? (
          <Alert variant="info" data-testid="country-edit-ceiling">{t('countries.ceiling.intro')}</Alert>
        ) : null}
        error={error}
        pending={pending}
        dirty={changed || !!note}
        size="lg"
        submitLabel={t('countries.edit.submit')}
        submitDisabled={!changed || invalid || !noteOk}
        onSubmit={submit}
        footerNote={footerNote}
        data-testid="country-edit"
      >
        {group.fields.map((spec) => (
          <FieldControl
            key={spec.field}
            spec={spec}
            value={draft[spec.field]}
            onChange={(v) => set(spec.field, v)}
            nullLabel={spec.kind === 'capability' ? t('countries.state.none') : nullLabel(spec)}
            suggestion={spec.type === 'dialCode' ? dialSuggestion : null}
            vocabulary={spec.field === 'insuranceCatalog'
              ? insuranceCatalogChoices(country, insurerCountries, row ? row.insuranceCatalog : null)
              : undefined}
            row={row}
            deployment={deployment}
            error={fieldErrors[spec.field] ? t(`countries.edit.invalid.${fieldErrors[spec.field]}`) : null}
            disabled={pending}
          />
        ))}

        <div data-testid="country-edit-note">
          <Textarea
            label={t('countries.edit.note')}
            required
            value={note}
            onChange={(e) => setNote(e.target?.value ?? e)}
            placeholder={t('countries.edit.notePlaceholder')}
            helperText={t('countries.edit.noteHelper')}
            maxLength={NOTE_MAX}
            showCount
            rows={2}
            disabled={pending}
          />
        </div>
      </TaskDialog>

      {/* Stacked above the dialog it guards — the one overlay stack §5.2 allows. */}
      <ConfirmDialog
        open={!!confirming}
        onOpenChange={(o) => { if (!o && !pending) setConfirming(null); }}
        title={t('countries.edit.confirm.title', { country: name })}
        description={(
          <div className="flex flex-col gap-3 text-sm leading-6">
            <p className="text-muted-foreground">{t('countries.edit.confirm.body')}</p>
            <ul className="flex list-disc flex-col gap-1.5 ps-5" data-testid="country-confirmations">
              {(confirming || []).map((c) => (
                <li key={c.token} data-testid={`country-confirmation-${c.token}`}>
                  <span className="font-semibold">{columnLabel(c.column)}</span>
                  {' — '}
                  {c.kind === 'removal'
                    ? t(`countries.edit.confirm.removal.${c.column}`, {
                      value: valueString({ field: c.field, type: 'enum' }, c.value), country: name,
                    })
                    : t(`countries.edit.confirm.change.${c.column}`, {
                      from: stated(c.spec, c.from), to: stated(c.spec, c.to), country: name,
                    })}
                  {isNotRead(c.field) ? ` ${t('countries.edit.confirm.notReadYet')}` : ''}
                </li>
              ))}
            </ul>
            {country === 'IR' ? (
              <Alert variant="warning" data-testid="country-confirm-ir">{t('countries.edit.confirm.iran')}</Alert>
            ) : null}
            <Checkbox
              checked={acknowledged}
              onChange={(v) => setAcknowledged(!!v)}
              label={t('countries.edit.confirm.acknowledge')}
              data-testid="country-confirm-ack"
            />
          </div>
        )}
        confirmLabel={t('countries.edit.confirm.submit')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={pending}
        confirmDisabled={!acknowledged}
        onConfirm={() => save(confirming || [])}
        data-testid="country-confirm"
        confirmTestId="country-confirm-submit"
        cancelTestId="country-confirm-cancel"
      />
    </>
  );
}

/** The label of a column, with «فعلاً خوانده نمی‌شود» when nothing reads it yet. */
function FieldLabel({ field }) {
  const { t } = useTranslation();
  return <>{t(`countries.fields.${field}`)}<NotReadBadge field={field} /></>;
}

/** Controls that share one name: a real fieldset and legend, its error under it. */
function Group({
  field, error, disabled, testId, children,
}) {
  const errorId = `${testId}-error`;
  return (
    <fieldset
      className="flex min-w-0 flex-col gap-2"
      disabled={disabled}
      aria-describedby={error ? errorId : undefined}
      data-testid={testId}
    >
      <legend className="mb-1.5 text-[11.5px] font-semibold text-muted-foreground"><FieldLabel field={field} /></legend>
      {children}
      {error ? <p id={errorId} role="alert" className="m-0 text-[11.5px] font-medium text-destructive">{error}</p> : null}
    </fieldset>
  );
}

/**
 * One column's control. Single values are a <select> or a text box where ''
 * is NULL; the weekend and the map have an explicit «پیرو * / خودش» switch,
 * because their NULL and their empty mean different things.
 */
function FieldControl({
  spec, value, onChange, nullLabel, suggestion, vocabulary: offered, row, deployment, error, disabled,
}) {
  const { t } = useTranslation();
  const valueString = useValueString();
  const testId = `country-edit-${spec.field}`;
  const label = <FieldLabel field={spec.field} />;
  const ceiling = spec.kind === 'capability'
    ? <CeilingNote spec={spec} value={row ? row[spec.field] : null} deployment={deployment} />
    : undefined;

  /** Options of a <select>: NULL first, then the vocabulary, then whatever the row holds outside it. */
  const options = (vocabulary) => {
    const known = [...vocabulary];
    if (value && !known.includes(value)) known.push(value);
    return [
      { value: '', label: nullLabel },
      ...known.map((v) => ({ value: String(v), label: valueString(spec, spec.type === 'weekday' ? Number(v) : v) })),
    ];
  };

  if (spec.type === 'enum' || spec.type === 'key' || spec.type === 'language' || spec.type === 'currency' || spec.type === 'weekday') {
    const vocabulary = offered || {
      language: LANGUAGES,
      currency: CURRENCY_CODES,
      weekday: WEEKDAYS.map(String),
    }[spec.type] || spec.options || [];
    return (
      <div data-testid={testId}>
        <Select
          label={label}
          value={value}
          onChange={(e) => onChange(e.target?.value ?? e)}
          options={options(vocabulary)}
          placeholder={null}
          helperText={ceiling}
          error={error || undefined}
          disabled={disabled}
        />
      </div>
    );
  }

  if (spec.type === 'timezone' || spec.type === 'dialCode') {
    return (
      <div data-testid={testId}>
        <TextInput
          label={label}
          value={value}
          onChange={(e) => onChange(e.target?.value ?? e)}
          dir="ltr"
          inputMode={spec.type === 'dialCode' ? 'numeric' : undefined}
          placeholder={t(`countries.edit.example.${spec.type}`)}
          helperText={error ? undefined : t('countries.edit.emptyMeans', { meaning: nullLabel })}
          error={error || undefined}
          disabled={disabled}
        />
        {suggestion && !String(value ?? '').trim() ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-1.5"
            onClick={() => onChange(suggestion)}
            disabled={disabled}
            data-testid={`${testId}-suggest`}
          >
            <span {...LTR_TEXT}>{`+${suggestion}`}</span>
            {t('countries.edit.suggested')}
          </Button>
        ) : null}
      </div>
    );
  }

  if (spec.type === 'list') {
    const known = [...spec.options, ...value.filter((v) => !spec.options.includes(v))];
    return (
      <Group field={spec.field} error={error} disabled={disabled} testId={testId}>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {known.map((v) => (
            <Checkbox
              key={v}
              checked={value.includes(v)}
              onChange={(on) => onChange(on ? [...value, v] : value.filter((x) => x !== v))}
              label={valueString(spec, v)}
              data-testid={`${testId}-${v}`}
            />
          ))}
        </div>
        <span className="text-[11.5px] text-muted-foreground">
          {value.length ? ceiling : t('countries.state.none')}
        </span>
      </Group>
    );
  }

  // Two shapes with their own «پیرو *» switch: the weekend (a list) and the map.
  const mode = value.follow ? 'follow' : 'own';
  const modeSwitch = (
    <SegmentedControl
      semantics="filter"
      size="sm"
      ariaLabel={t(`countries.fields.${spec.field}`)}
      options={[
        { value: 'follow', label: nullLabel },
        { value: 'own', label: t('countries.edit.own') },
      ]}
      value={mode}
      onChange={(m) => onChange({ ...value, follow: m === 'follow' })}
      data-testid={`${testId}-mode`}
    />
  );

  if (spec.type === 'weekdays') {
    return (
      <Group field={spec.field} error={error} disabled={disabled} testId={testId}>
        {modeSwitch}
        {!value.follow ? (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {WEEKDAYS.map((d) => (
                <Checkbox
                  key={d}
                  checked={value.days.includes(d)}
                  onChange={(on) => onChange({
                    ...value, days: on ? [...value.days, d] : value.days.filter((x) => x !== d),
                  })}
                  label={valueString({ type: 'weekday' }, d)}
                  disabled={disabled}
                  data-testid={`${testId}-${d}`}
                />
              ))}
            </div>
            {!value.days.length ? (
              <span className="text-[11.5px] text-muted-foreground">{t('countries.state.noWeekend')}</span>
            ) : null}
          </>
        ) : null}
      </Group>
    );
  }

  // mapView
  const coord = (key, labelKey) => (
    <TextInput
      label={t(labelKey)}
      value={value[key]}
      onChange={(e) => onChange({ ...value, [key]: e.target?.value ?? e })}
      dir="ltr"
      inputMode="decimal"
      disabled={disabled}
      data-testid={`${testId}-${key}`}
    />
  );
  return (
    <Group field={spec.field} error={error} disabled={disabled} testId={testId}>
      {modeSwitch}
      {!value.follow ? (
        <div className="grid grid-cols-3 gap-3">
          {coord('lat', 'countries.edit.lat')}
          {coord('lon', 'countries.edit.lon')}
          {coord('zoom', 'countries.edit.zoom')}
        </div>
      ) : null}
    </Group>
  );
}
